import type {
  Asset,
  AssetId,
  DemoState,
  MethodSnapshot,
  PatternAssetId,
  RegionConfig,
  RegionEntry,
  RegionStack,
  StampMaskAssetId,
} from '../state';
import { buildMethodSnapshot } from '../util/method-patch';
import { decodeZip, decodeZipText, encodeZip, type ZipEntryInput } from './zip-store';

export type BundleExtension = 'amtp' | 'amtn' | 'amtt';
export type BundleVersion = 1;

export type EncodedImageData = {
  width: number;
  height: number;
  data: string;
};

export type EmbeddedAssetInput = {
  asset: Asset;
  image: ImageData;
};

export type EmbeddedAsset = Asset & {
  image: EncodedImageData;
};

export type DecodedAssetBundle = {
  assets: Asset[];
  assetData: Map<string, ImageData>;
};

export type PresetBundleInput = {
  name?: string;
  preset: MethodSnapshot;
  assets?: readonly EmbeddedAssetInput[];
};

export type PresetBundleDocument = {
  format: 'amitone-preset';
  extension: 'amtp';
  version: BundleVersion;
  name: string | null;
  preset: MethodSnapshot;
  assets: EmbeddedAsset[];
};

export type DecodedPresetBundle = Omit<PresetBundleDocument, 'assets'> & DecodedAssetBundle;

export type TemplateBinding = {
  templateRegionId: string;
  rgb: [number, number, number];
  stackId: string | null;
  boundRegionUuid: string | null;
  boundLabel: number | null;
  candidateRegionUuids?: string[];
};

export type RegionTemplateGroupInput = {
  templateRegionId: string;
  rgb: [number, number, number];
  preset: MethodSnapshot | null;
  stackId: string | null;
};

export type RegionTemplateStackInput = {
  templateStackId: string;
  userName: string;
  preset: MethodSnapshot | null;
};

export type RegionTemplateBundleInput = {
  name?: string;
  groups: readonly RegionTemplateGroupInput[];
  stacks?: readonly RegionTemplateStackInput[];
  bindings?: readonly TemplateBinding[];
  assets?: readonly EmbeddedAssetInput[];
};

export type RegionTemplateBundleDocument = {
  format: 'amitone-region-template';
  extension: 'amtt';
  version: BundleVersion;
  name: string | null;
  groups: RegionTemplateGroupInput[];
  stacks: RegionTemplateStackInput[];
  bindings: TemplateBinding[];
  assets: EmbeddedAsset[];
};

export type DecodedRegionTemplateBundle = Omit<RegionTemplateBundleDocument, 'assets'> &
  DecodedAssetBundle;

export type TemplateStackState = {
  activeTemplateId: string | null;
  name: string | null;
  groups: RegionTemplateGroupInput[];
  stacks: RegionTemplateStackInput[];
  bindings: TemplateBinding[];
};

export type ReferenceOverlayDocument = {
  image: EncodedImageData | null;
  opacity: number;
  enabled: boolean;
};

export type ProjectTransformParams = {
  shapeFlipX: boolean;
  shapeFlipY: boolean;
  patternRotationDeg: number;
  patternFlipX: boolean;
  patternFlipY: boolean;
  patternHalftoneShapeFlipX: boolean;
  patternHalftoneShapeFlipY: boolean;
};

export type ProjectBundleInput = {
  state: DemoState;
  assets?: readonly EmbeddedAssetInput[];
  templateStackState?: Partial<TemplateStackState>;
};

export type ProjectBundleDocument = {
  format: 'amitone-project';
  extension: 'amtn';
  version: BundleVersion;
  source: EncodedImageData | null;
  sourceOriginal: EncodedImageData | null;
  labelMap: EncodedImageData | null;
  regions: {
    regions: RegionConfig[];
    regionEntries: RegionEntry[];
    regionStacks: RegionStack[];
    regionLabelPlane: EncodedByteArray;
    activeRegionUuids: string[];
    activeStackUuid: string | null;
    remap: {
      maxK: number;
      deltaE: number;
      preset: DemoState['remapPreset'];
      useRawLabelmap: boolean;
    };
  };
  presets: {
    global: MethodSnapshot;
    regionOverrides: Array<{ uuid: string; preset: MethodSnapshot }>;
    stackOverrides: Array<{ uuid: string; preset: MethodSnapshot }>;
    tonePreset: string;
  };
  referenceOverlay: ReferenceOverlayDocument;
  assets: EmbeddedAsset[];
  assetLibrary: Asset[];
  transform: ProjectTransformParams;
  templateStackState: TemplateStackState;
  sourceAdjust: {
    global: { brightness: number; contrast: number };
    scope: DemoState['sourceAdjustScope'];
    regionSourceAdjust: DemoState['regionSourceAdjust'];
  };
  undoHistory: null;
};

export type DecodedProjectBundle = Omit<
  ProjectBundleDocument,
  'source' | 'sourceOriginal' | 'labelMap' | 'referenceOverlay' | 'regions' | 'assets'
> &
  DecodedAssetBundle & {
    source: ImageData | null;
    sourceOriginal: ImageData | null;
    labelMap: ImageData | null;
    referenceOverlay: {
      image: ImageData | null;
      opacity: number;
      enabled: boolean;
    };
    regions: Omit<ProjectBundleDocument['regions'], 'regionLabelPlane'> & {
      regionLabelPlane: Uint8Array;
    };
  };

export type BundlePayload = string | ArrayBuffer | Uint8Array;

type ArchiveManifest = {
  format: PresetBundleDocument['format'] | RegionTemplateBundleDocument['format'] | ProjectBundleDocument['format'];
  extension: BundleExtension;
  version: BundleVersion;
  document: 'preset.json' | 'template.json' | 'project.json';
};

type ArchiveAsset = Asset & {
  imageRef: string;
};

type ArchiveImageRef = {
  ref: string;
};

type EncodedByteArray = {
  data: string;
};

const CHUNK_SIZE = 0x8000;

function bytesToBase64(bytes: Uint8Array | Uint8ClampedArray): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
    const chunk = bytes.subarray(i, i + CHUNK_SIZE);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

function base64ToBytes(data: string): Uint8Array {
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function encodeImageData(image: ImageData): EncodedImageData {
  return {
    width: image.width,
    height: image.height,
    data: bytesToBase64(image.data),
  };
}

export function decodeImageData(encoded: EncodedImageData): ImageData {
  const bytes = base64ToBytes(encoded.data);
  return new ImageData(new Uint8ClampedArray(bytes), encoded.width, encoded.height);
}

function encodeByteArray(bytes: Uint8Array): EncodedByteArray {
  return { data: bytesToBase64(bytes) };
}

function decodeByteArray(encoded: EncodedByteArray): Uint8Array {
  return base64ToBytes(encoded.data);
}

function encodeAssets(inputs: readonly EmbeddedAssetInput[] = []): EmbeddedAsset[] {
  return inputs.map(({ asset, image }) => ({
    ...asset,
    image: encodeImageData(image),
  }));
}

function decodeAssets(assets: readonly EmbeddedAsset[]): DecodedAssetBundle {
  const assetData = new Map<string, ImageData>();
  const assetLibrary: Asset[] = [];
  for (const embedded of assets) {
    const { image, ...asset } = embedded;
    assetLibrary.push(asset);
    assetData.set(asset.id, decodeImageData(image));
  }
  return { assets: assetLibrary, assetData };
}

function parseJson(value: string): unknown {
  return JSON.parse(value) as unknown;
}

function isBinaryPayload(value: BundlePayload): value is ArrayBuffer | Uint8Array {
  return typeof value !== 'string';
}

function binaryPayloadToBytes(value: ArrayBuffer | Uint8Array): Uint8Array {
  return value instanceof Uint8Array ? value : new Uint8Array(value);
}

function parseBinaryJson(value: ArrayBuffer | Uint8Array): unknown {
  return parseJson(decodeZipText(binaryPayloadToBytes(value)));
}

function parseZipJson<T>(entries: ReadonlyMap<string, Uint8Array>, path: string): T {
  const entry = entries.get(path);
  if (entry === undefined) {
    throw new Error(`Missing ZIP entry: ${path}`);
  }
  return JSON.parse(decodeZipText(entry)) as T;
}

function assetImagePath(asset: Asset): string {
  const suffix = asset.contentHash.replace(/[^a-z0-9_-]/gi, '_');
  return `assets/${asset.kind}-${suffix}.json`;
}

function stripEmbeddedAsset(asset: EmbeddedAsset): ArchiveAsset {
  const { image: _image, ...meta } = asset;
  return {
    ...meta,
    imageRef: assetImagePath(meta),
  };
}

function embeddedAssetsFromArchive(
  entries: ReadonlyMap<string, Uint8Array>,
  assets: readonly ArchiveAsset[],
): EmbeddedAsset[] {
  return assets.map((asset) => {
    const { imageRef, ...meta } = asset;
    return {
      ...meta,
      image: parseZipJson<EncodedImageData>(entries, imageRef),
    };
  });
}

function addAssetEntries(entries: ZipEntryInput[], assets: readonly EmbeddedAsset[]): ArchiveAsset[] {
  const archiveAssets = assets.map(stripEmbeddedAsset);
  for (const asset of assets) {
    entries.push({
      path: assetImagePath(asset),
      data: JSON.stringify(asset.image, null, 2),
    });
  }
  return archiveAssets;
}

function imageRef(path: string, image: EncodedImageData | null): ArchiveImageRef | null {
  return image === null ? null : { ref: path };
}

function addOptionalImageEntry(
  entries: ZipEntryInput[],
  path: string,
  image: EncodedImageData | null,
): ArchiveImageRef | null {
  if (image === null) return null;
  entries.push({ path, data: JSON.stringify(image, null, 2) });
  return imageRef(path, image);
}

function assertBundle(
  value: unknown,
  format: string,
  extension: BundleExtension,
): asserts value is { format: string; extension: BundleExtension; version: BundleVersion } {
  if (typeof value !== 'object' || value === null) {
    throw new Error('Invalid amitone bundle');
  }
  const record = value as Record<string, unknown>;
  if (record['format'] !== format || record['extension'] !== extension || record['version'] !== 1) {
    throw new Error(`Unsupported ${extension} bundle`);
  }
}

function collectReferencedAssetIds(...presets: Array<MethodSnapshot | null>): Set<AssetId> {
  const ids = new Set<AssetId>();
  for (const preset of presets) {
    if (preset === null) continue;
    if (preset.patternRefId !== null) ids.add(preset.patternRefId);
    if (preset.stampMaskRefId !== null) ids.add(preset.stampMaskRefId);
  }
  return ids;
}

function filterReferencedAssets(
  assets: readonly EmbeddedAssetInput[] | undefined,
  referencedIds: ReadonlySet<AssetId>,
): EmbeddedAsset[] {
  if (assets === undefined) return [];
  return encodeAssets(assets.filter(({ asset }) => referencedIds.has(asset.id)));
}

function createPresetBundleDocument(input: PresetBundleInput): PresetBundleDocument {
  const referencedIds = collectReferencedAssetIds(input.preset);
  return {
    format: 'amitone-preset',
    extension: 'amtp',
    version: 1,
    name: input.name ?? null,
    preset: input.preset,
    assets: filterReferencedAssets(input.assets, referencedIds),
  };
}

function archiveManifest(document: ArchiveManifest): ZipEntryInput {
  return {
    path: 'manifest.json',
    data: JSON.stringify(document, null, 2),
  };
}

function encodePresetDocumentArchive(document: PresetBundleDocument): Uint8Array {
  const entries: ZipEntryInput[] = [
    archiveManifest({
      format: document.format,
      extension: document.extension,
      version: document.version,
      document: 'preset.json',
    }),
  ];
  const archiveDocument = {
    ...document,
    assets: addAssetEntries(entries, document.assets),
  };
  entries.push({ path: 'preset.json', data: JSON.stringify(archiveDocument, null, 2) });
  return encodeZip(entries);
}

export function encodePresetBundle(input: PresetBundleInput): string {
  return JSON.stringify(createPresetBundleDocument(input));
}

export function encodePresetBundleArchive(input: PresetBundleInput): Uint8Array {
  return encodePresetDocumentArchive(createPresetBundleDocument(input));
}

function decodePresetDocument(input: BundlePayload): PresetBundleDocument {
  if (!isBinaryPayload(input)) {
    const parsed = parseJson(input);
    assertBundle(parsed, 'amitone-preset', 'amtp');
    return parsed as PresetBundleDocument;
  }
  let entries: Map<string, Uint8Array>;
  try {
    entries = decodeZip(input);
  } catch {
    const parsed = parseBinaryJson(input);
    assertBundle(parsed, 'amitone-preset', 'amtp');
    return parsed as PresetBundleDocument;
  }
  const manifest = parseZipJson<ArchiveManifest>(entries, 'manifest.json');
  assertBundle(manifest, 'amitone-preset', 'amtp');
  const document = parseZipJson<Omit<PresetBundleDocument, 'assets'> & { assets: ArchiveAsset[] }>(
    entries,
    manifest.document,
  );
  return {
    ...document,
    assets: embeddedAssetsFromArchive(entries, document.assets),
  };
}

export function decodePresetBundle(input: BundlePayload): DecodedPresetBundle {
  const document = decodePresetDocument(input);
  const decoded = decodeAssets(document.assets);
  return {
    format: document.format,
    extension: document.extension,
    version: document.version,
    name: document.name,
    preset: document.preset,
    ...decoded,
  };
}

function defaultTemplateBindings(groups: readonly RegionTemplateGroupInput[]): TemplateBinding[] {
  return groups.map((group) => ({
    templateRegionId: group.templateRegionId,
    rgb: group.rgb,
    stackId: group.stackId,
    boundRegionUuid: null,
    boundLabel: null,
    candidateRegionUuids: [],
  }));
}

function createRegionTemplateBundleDocument(
  input: RegionTemplateBundleInput,
): RegionTemplateBundleDocument {
  const presets = [
    ...input.groups.map((group) => group.preset),
    ...(input.stacks ?? []).map((stack) => stack.preset),
  ];
  const referencedIds = collectReferencedAssetIds(...presets);
  return {
    format: 'amitone-region-template',
    extension: 'amtt',
    version: 1,
    name: input.name ?? null,
    groups: input.groups.slice(),
    stacks: input.stacks?.slice() ?? [],
    bindings: input.bindings?.slice() ?? defaultTemplateBindings(input.groups),
    assets: filterReferencedAssets(input.assets, referencedIds),
  };
}

function encodeRegionTemplateDocumentArchive(
  document: RegionTemplateBundleDocument,
): Uint8Array {
  const entries: ZipEntryInput[] = [
    archiveManifest({
      format: document.format,
      extension: document.extension,
      version: document.version,
      document: 'template.json',
    }),
  ];
  const archiveDocument = {
    ...document,
    assets: addAssetEntries(entries, document.assets),
  };
  entries.push({ path: 'template.json', data: JSON.stringify(archiveDocument, null, 2) });
  return encodeZip(entries);
}

export function encodeRegionTemplateBundle(input: RegionTemplateBundleInput): string {
  return JSON.stringify(createRegionTemplateBundleDocument(input));
}

export function encodeRegionTemplateBundleArchive(input: RegionTemplateBundleInput): Uint8Array {
  return encodeRegionTemplateDocumentArchive(createRegionTemplateBundleDocument(input));
}

function decodeRegionTemplateDocument(input: BundlePayload): RegionTemplateBundleDocument {
  if (!isBinaryPayload(input)) {
    const parsed = parseJson(input);
    assertBundle(parsed, 'amitone-region-template', 'amtt');
    return parsed as RegionTemplateBundleDocument;
  }
  let entries: Map<string, Uint8Array>;
  try {
    entries = decodeZip(input);
  } catch {
    const parsed = parseBinaryJson(input);
    assertBundle(parsed, 'amitone-region-template', 'amtt');
    return parsed as RegionTemplateBundleDocument;
  }
  const manifest = parseZipJson<ArchiveManifest>(entries, 'manifest.json');
  assertBundle(manifest, 'amitone-region-template', 'amtt');
  const document = parseZipJson<
    Omit<RegionTemplateBundleDocument, 'assets'> & { assets: ArchiveAsset[] }
  >(entries, manifest.document);
  return {
    ...document,
    assets: embeddedAssetsFromArchive(entries, document.assets),
  };
}

export function decodeRegionTemplateBundle(input: BundlePayload): DecodedRegionTemplateBundle {
  const document = decodeRegionTemplateDocument(input);
  const decoded = decodeAssets(document.assets);
  return {
    format: document.format,
    extension: document.extension,
    version: document.version,
    name: document.name,
    groups: document.groups,
    stacks: document.stacks,
    bindings: document.bindings,
    ...decoded,
  };
}

function projectTransformFromState(state: DemoState): ProjectTransformParams {
  return {
    shapeFlipX: state.shapeFlipX,
    shapeFlipY: state.shapeFlipY,
    patternRotationDeg: state.patternRotationDeg,
    patternFlipX: state.patternFlipX,
    patternFlipY: state.patternFlipY,
    patternHalftoneShapeFlipX: state.patternHalftoneShapeFlipX,
    patternHalftoneShapeFlipY: state.patternHalftoneShapeFlipY,
  };
}

function projectReferenceOverlayFromState(state: DemoState): ReferenceOverlayDocument {
  return {
    image:
      state.referenceOverlay.image === null
        ? null
        : encodeImageData(state.referenceOverlay.image),
    opacity: state.referenceOverlay.opacity,
    enabled: state.referenceOverlay.enabled,
  };
}

function templateStackStateFromInput(input?: Partial<TemplateStackState>): TemplateStackState {
  return {
    activeTemplateId: input?.activeTemplateId ?? null,
    name: input?.name ?? null,
    groups: input?.groups?.slice() ?? [],
    stacks: input?.stacks?.slice() ?? [],
    bindings: input?.bindings?.slice() ?? [],
  };
}

function findAssetRefId(
  state: DemoState,
  kind: 'pattern',
  image: ImageData | null,
): PatternAssetId | null;
function findAssetRefId(
  state: DemoState,
  kind: 'stampMask',
  image: ImageData | null,
): StampMaskAssetId | null;
function findAssetRefId(
  state: DemoState,
  kind: Asset['kind'],
  image: ImageData | null,
): AssetId | null {
  if (image === null) return null;
  for (const asset of state.assetLibrary) {
    if (asset.kind !== kind) continue;
    if (state.assetData.get(asset.id) === image) return asset.id;
  }
  return null;
}

function createProjectBundleDocument(input: ProjectBundleInput): ProjectBundleDocument {
  const { state } = input;
  const globalPreset = buildMethodSnapshot(
    state,
    findAssetRefId(state, 'stampMask', state.customStampMask),
    findAssetRefId(state, 'pattern', state.patternImage),
  );
  const regionOverrides = state.regionEntries.flatMap((entry) =>
    entry.override === null ? [] : [{ uuid: entry.uuid, preset: entry.override }],
  );
  const stackOverrides = state.regionStacks.flatMap((stack) =>
    stack.override === null ? [] : [{ uuid: stack.uuid, preset: stack.override }],
  );
  const document: ProjectBundleDocument = {
    format: 'amitone-project',
    extension: 'amtn',
    version: 1,
    source: state.source === null ? null : encodeImageData(state.source),
    sourceOriginal: state.sourceOriginal === null ? null : encodeImageData(state.sourceOriginal),
    labelMap: state.labelMap === null ? null : encodeImageData(state.labelMap),
    regions: {
      regions: state.regions.slice(),
      regionEntries: state.regionEntries.slice(),
      regionStacks: state.regionStacks.slice(),
      regionLabelPlane: encodeByteArray(state.regionLabelPlane),
      activeRegionUuids: state.activeRegionUuids.slice(),
      activeStackUuid: state.activeStackUuid,
      remap: {
        maxK: state.remapMaxK,
        deltaE: state.remapDeltaE,
        preset: state.remapPreset,
        useRawLabelmap: state.useRawLabelmap,
      },
    },
    presets: {
      global: globalPreset,
      regionOverrides,
      stackOverrides,
      tonePreset: state.tonePreset,
    },
    referenceOverlay: projectReferenceOverlayFromState(state),
    assets: encodeAssets(input.assets),
    assetLibrary: state.assetLibrary.slice(),
    transform: projectTransformFromState(state),
    templateStackState: templateStackStateFromInput(input.templateStackState),
    sourceAdjust: {
      global: {
        brightness: state.sourceBrightness,
        contrast: state.sourceContrast,
      },
      scope: state.sourceAdjustScope,
      regionSourceAdjust: { ...state.regionSourceAdjust },
    },
    undoHistory: null,
  };
  return document;
}

type ArchiveProjectDocument = Omit<
  ProjectBundleDocument,
  'source' | 'sourceOriginal' | 'labelMap' | 'referenceOverlay' | 'assets'
> & {
  source: ArchiveImageRef | null;
  sourceOriginal: ArchiveImageRef | null;
  labelMap: ArchiveImageRef | null;
  referenceOverlay: Omit<ReferenceOverlayDocument, 'image'> & {
    image: ArchiveImageRef | null;
  };
  assets: ArchiveAsset[];
};

function archiveImageFromRef(
  entries: ReadonlyMap<string, Uint8Array>,
  ref: ArchiveImageRef | EncodedImageData | null,
): EncodedImageData | null {
  if (ref === null) return null;
  if ('ref' in ref) return parseZipJson<EncodedImageData>(entries, ref.ref);
  return ref;
}

function encodeProjectDocumentArchive(document: ProjectBundleDocument): Uint8Array {
  const entries: ZipEntryInput[] = [
    archiveManifest({
      format: document.format,
      extension: document.extension,
      version: document.version,
      document: 'project.json',
    }),
  ];
  const archiveDocument: ArchiveProjectDocument = {
    ...document,
    source: addOptionalImageEntry(entries, 'images/source.json', document.source),
    sourceOriginal: addOptionalImageEntry(
      entries,
      'images/source-original.json',
      document.sourceOriginal,
    ),
    labelMap: addOptionalImageEntry(entries, 'images/label-map.json', document.labelMap),
    referenceOverlay: {
      ...document.referenceOverlay,
      image: addOptionalImageEntry(
        entries,
        'images/reference-overlay.json',
        document.referenceOverlay.image,
      ),
    },
    assets: addAssetEntries(entries, document.assets),
  };
  entries.push({ path: 'project.json', data: JSON.stringify(archiveDocument, null, 2) });
  return encodeZip(entries);
}

export function encodeProjectBundle(input: ProjectBundleInput): string {
  return JSON.stringify(createProjectBundleDocument(input));
}

export function encodeProjectBundleArchive(input: ProjectBundleInput): Uint8Array {
  return encodeProjectDocumentArchive(createProjectBundleDocument(input));
}

function decodeProjectDocument(input: BundlePayload): ProjectBundleDocument {
  if (!isBinaryPayload(input)) {
    const parsed = parseJson(input);
    assertBundle(parsed, 'amitone-project', 'amtn');
    return parsed as ProjectBundleDocument;
  }
  let entries: Map<string, Uint8Array>;
  try {
    entries = decodeZip(input);
  } catch {
    const parsed = parseBinaryJson(input);
    assertBundle(parsed, 'amitone-project', 'amtn');
    return parsed as ProjectBundleDocument;
  }
  const manifest = parseZipJson<ArchiveManifest>(entries, 'manifest.json');
  assertBundle(manifest, 'amitone-project', 'amtn');
  const document = parseZipJson<ArchiveProjectDocument>(entries, manifest.document);
  return {
    ...document,
    source: archiveImageFromRef(entries, document.source),
    sourceOriginal: archiveImageFromRef(entries, document.sourceOriginal),
    labelMap: archiveImageFromRef(entries, document.labelMap),
    referenceOverlay: {
      ...document.referenceOverlay,
      image: archiveImageFromRef(entries, document.referenceOverlay.image),
    },
    assets: embeddedAssetsFromArchive(entries, document.assets),
  };
}

export function decodeProjectBundle(input: BundlePayload): DecodedProjectBundle {
  const document = decodeProjectDocument(input);
  const decoded = decodeAssets(document.assets);
  return {
    format: document.format,
    extension: document.extension,
    version: document.version,
    source: document.source === null ? null : decodeImageData(document.source),
    sourceOriginal:
      document.sourceOriginal === null ? null : decodeImageData(document.sourceOriginal),
    labelMap: document.labelMap === null ? null : decodeImageData(document.labelMap),
    regions: {
      ...document.regions,
      regionLabelPlane: decodeByteArray(document.regions.regionLabelPlane),
    },
    presets: document.presets,
    referenceOverlay: {
      image:
        document.referenceOverlay.image === null
          ? null
          : decodeImageData(document.referenceOverlay.image),
      opacity: document.referenceOverlay.opacity,
      enabled: document.referenceOverlay.enabled,
    },
    assetLibrary: document.assetLibrary,
    transform: document.transform,
    templateStackState: document.templateStackState,
    sourceAdjust: document.sourceAdjust,
    undoHistory: null,
    ...decoded,
  };
}
